/**
 * Who may create, edit, and delete **zones** (the territory records + their H3
 * cells) on Master · Zone.
 *
 * Managing a zone's **mitra roster** is deliberately NOT gated — that's routine
 * ops work any signed-in user does. Only the zone record itself is restricted,
 * because a wrong cell edit or a deleted zone silently changes how every
 * dispatch plans.
 *
 * `REACT_APP_ZONE_EDITOR_EMAILS`:
 *   - `all`               → every signed-in user may edit zones
 *   - `a@dash.com,b@…`    → only those addresses (comma-separated, case- and
 *                           whitespace-insensitive)
 *   - unset / empty       → treated as `all`
 *
 * The unset-means-open default matches `REACT_APP_ALLOWED_EMAIL_DOMAIN`
 * (see `config/app.ts`): restriction is opt-in, so local dev and any
 * environment that hasn't set the variable keeps working. The flip side is
 * that a **forgotten variable in staging/prod leaves zone editing open to
 * everyone** — set it explicitly in both.
 *
 * ⚠️ This is a UI affordance, not a security boundary. The bundle is public and
 * the value is baked in at build time, so anyone can read the list and call the
 * API directly. Real enforcement is the service's `ADMIN` requirement on
 * `POST /v1/zones`, `PATCH /v1/zones/:id`, `DELETE /v1/zones/:id`, and the
 * cell routes.
 */

const RAW_ALLOWLIST = process.env.REACT_APP_ZONE_EDITOR_EMAILS ?? '';

interface ZoneEditorAllowlist {
  /** True when every signed-in user is allowed. */
  allowAll: boolean;
  /** Lowercased addresses, empty when `allowAll`. */
  emails: Set<string>;
}

const parseAllowlist = (raw: string): ZoneEditorAllowlist => {
  const tokens = raw
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);

  // Empty means "not configured" → open, same convention as the login-domain
  // restriction. An explicit `all` anywhere in the list also opens it, so
  // `all` never gets mistaken for an address.
  if (tokens.length === 0 || tokens.includes('all')) {
    return { allowAll: true, emails: new Set() };
  }
  return { allowAll: false, emails: new Set(tokens) };
};

const ALLOWLIST = parseAllowlist(RAW_ALLOWLIST);

/** True when zone editing is open to everyone (no allowlist configured). */
export const isZoneEditingOpen = (): boolean => ALLOWLIST.allowAll;

/** May this user create / edit / delete zones and paint cells? */
export const canEditZones = (email?: string | null): boolean => {
  if (ALLOWLIST.allowAll) return true;
  if (!email) return false;
  return ALLOWLIST.emails.has(email.trim().toLowerCase());
};

/** The configured addresses, for a "who can do this?" hint in the UI. */
export const zoneEditorEmails = (): string[] =>
  Array.from(ALLOWLIST.emails).sort();
